<template>
  <div>
    <span class="p-float-label">
      <MultiSelect
        v-if="multiple"
        :id="fieldId"
        :inputId="fieldId"
        :name="fieldName"
        v-model="modelValue"
        :class="[{ 'p-invalid': hasError }, 'form-control']"
        :options="filteredOptions"
        :optionLabel="getOptionLabel"
        :optionValue="optionValue"
        :dataKey="optionValue"
        :showClear="showClear"
        :filter="true"
        :disabled="disabled"
        display="chip"
        @filter="handleFilter"
        :emptyFilterMessage="loading ? 'A pesquisar...' : 'Nenhum resultado encontrado'"
      >
        <template #option="slotProps">
           <slot name="option" :option="slotProps.option">
              {{ getOptionLabel(slotProps.option) }}
            </slot>
        </template>
      </MultiSelect>

      <Dropdown
        v-else
        :id="fieldId"
        :inputId="fieldId"
        :name="fieldName"
        v-model="modelValue"
        :class="[{ 'p-invalid': hasError }, 'form-control']"
        :options="options"
        :optionLabel="getOptionLabel"
        :optionValue="optionValue"
        :dataKey="optionValue"
        :showClear="showClear"
        :filter="true"
        :disabled="disabled"
        @filter="handleFilter"
        @change="handleChange"
        :appendTo="appendTo"
        :required="required"
        :emptyFilterMessage="loading ? 'A pesquisar...' : 'Nenhum resultado encontrado'"
      >
        <template #value>
          <span class="label-input-selected">{{ getOptionLabel(currentSelected) }}</span>
        </template>

        <template #option="slotProps" v-if="!loading">
          <span
            v-if="$isMobile() && slotProps.option[optionValue]"
            class="options-search-responsive"
          >
            <slot name="option" :option="slotProps.option">
              {{ getOptionLabel(slotProps.option) }}
            </slot>
          </span>
          <span v-else-if="slotProps.option[optionValue]">
            <slot name="option" :option="slotProps.option">
              {{ getOptionLabel(slotProps.option) }}
            </slot>
          </span>
        </template>

        <template #option="" v-else>
          <slot name="empty">
            {{ loading ? 'Carregando...' : 'Nenhum resultado encontrado' }}
          </slot>
        </template>
      </Dropdown>

      <label :for="fieldId">{{ label }}</label>
    </span>
  </div>
</template>

<script>
const generateId = () => {
    return Math.random(2) * Date.now()
}

export default {
  name: "AppAutocomplete",
  inject: {
    $validator: {
      default: null
    }
  },
  props: {
    label: { type: String, default: '' },
    value: { type: [String, Number, Object, Array], default: null },
    fetchFunction: { type: Function, required: true },
    optionLabel: { type: [String, Function], default: 'name' },
    optionValue: { type: String, default: 'id' },
    multiple: {
      type: Boolean,
      default: false,
    },
    resolveItemsFunction: {
      type: Function,
      default: null,
    },
    editModel: {
      type: [Object, Function],
      default: null
    },
    fieldId: {
      type: String,
      default: () => `autocomplete-${generateId()}`,
    },
    fieldName: {
      type: String,
      default: function() { return this.fieldId; }
    },
    showClear: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false },
    required: { type: Boolean, default: false },
    minSearchLength: { type: Number, default: 2 },
    searchDelay: { type: Number, default: 300 },
    loadOnMount: { type: Boolean, default: true },
    appendTo: { type: String, default: null },
  },

  data() {
    return {
      modelValue: this.multiple ? [] : null,
      currentSelected: null,
      options: [],
      selectedItems: [],
      loading: false,
      searchTimeout: null,
      lastSearchTerm: '',
      isInitialized: false,
    };
  },

  computed: {
    getOptionLabel() {
      if (typeof this.optionLabel === 'function') {
        return this.optionLabel;
      }
      return (option) => {
        if (!option) return '';
        if (typeof this.optionLabel === 'string') {
          return option[this.optionLabel] || '';
        }
        return option.name || option.label || option[this.optionValue] || '';
      };
    },

    filteredOptions() {
      if (!this.multiple) {
        return this.options;
      }

      const selectedIds = Array.isArray(this.modelValue) ? this.modelValue : [];
      const optionIds = new Set(this.options.map(opt => opt[this.optionValue]));

      const missingSelectedItems = this.selectedItems.filter(item =>
        selectedIds.includes(item[this.optionValue]) &&
        !optionIds.has(item[this.optionValue])
      );

      return [...missingSelectedItems, ...this.options];
    },

    hasError() {
      if (this.$validator && this.errors) {
        return this.errors.has(this.fieldName);
      }
      return false;
    }
  },

  watch: {
    modelValue(newValue) {
      if (!this.multiple) {
        this.currentSelected = this.options.find(o => o[this.optionValue] === newValue);
      } else {
        this.updateSelectedItems();
      }
      this.$emit("input", newValue);
    },
    value: {
      immediate: true,
      async handler(newValue, oldValue) {
        if (this.multiple) {
          if (
            Array.isArray(newValue) &&
            newValue.length > 0 &&
            newValue[0] !== null &&
            typeof newValue[0] === 'object'
          ) {
            this.selectedItems = [...newValue];
            this.modelValue = newValue.map(item => item[this.optionValue]);
          } else {
            this.modelValue = Array.isArray(newValue) ? newValue : [];
          }
        } else {
          this.modelValue = newValue;
        }

        if (this.isInitialized && newValue && newValue !== oldValue) {
            const valueAsArray = Array.isArray(newValue) ? newValue : [newValue];
            const allItemsAvailable = valueAsArray.every(val =>
                this.options.some(opt => opt[this.optionValue] === val)
            );

            if (!allItemsAvailable && this.editModel) {
                let addValue = await this.resolveInitialItems(newValue);
                if (!this.multiple) {
                    this.currentSelected = addValue[0];
                } else {
                    this.currentSelected = addValue;
                    this.selectedItems = addValue;
                }
            }
        }
      }
    },
  },

  async mounted() {
    if (this.loadOnMount || this.modelValue) {
      await this.loadData();
    }
    this.isInitialized = true;
  },

  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },

  methods: {
    async loadData(searchTerm = '') {
      this.loading = true;
      try {
        const fetchedData = await this.fetchFunction(searchTerm);
        let finalData = Array.isArray(fetchedData) ? fetchedData : [];

        if (this.isInitialized === false && this.modelValue) {
          const initialItems = await this.resolveInitialItems();

          initialItems.forEach(item => {
            if (item && !finalData.some(option => option[this.optionValue] === item[this.optionValue])) {
              finalData.unshift(item);
            }
          });

          if (!this.multiple) {
            this.currentSelected = initialItems[0];
          } else {
            this.selectedItems = initialItems;
          }
        }

        this.options = finalData;
        this.lastSearchTerm = searchTerm;

        if (this.multiple) {
          this.updateSelectedItems();
        }
      } catch (error) {
        console.error('Error loading autocomplete data:', error);
        this.options = [];
        this.$emit('error', error);
      } finally {
        this.loading = false;
      }
    },

    updateSelectedItems() {
      if (!this.multiple || !Array.isArray(this.modelValue)) {
        return;
      }

      const selectedIds = this.modelValue;
      const allOptions = [...this.selectedItems, ...this.options];

      const uniqueMap = new Map();
      allOptions.forEach(item => {
        if (selectedIds.includes(item[this.optionValue])) {
          uniqueMap.set(item[this.optionValue], item);
        }
      });

      this.selectedItems = Array.from(uniqueMap.values());
    },

    async resolveInitialItems() {
      if (!this.multiple && this.editModel) {
        const item = typeof this.editModel === 'function' ? await this.editModel(this.modelValue) : this.editModel;
        return item ? [item] : [];
      }
      if (this.multiple && this.resolveItemsFunction && this.modelValue?.length > 0) {
        return await this.resolveItemsFunction(this.modelValue);
      }
      return [];
    },

    handleFilter(event) {
      const searchTerm = event.value || '';

      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      if (searchTerm.length > 0 && searchTerm.length < this.minSearchLength) {
        return;
      }

      if (searchTerm === this.lastSearchTerm) {
        return;
      }

      this.loading = true;

      this.searchTimeout = setTimeout(() => {
        this.loadData(searchTerm);
      }, this.searchDelay);
    },

    handleChange(event) {
      this.$emit('selection-change', event.value);
      if (event.originalEvent.currentTarget.classList.contains('p-dropdown-clear-icon')) {
        this.$emit('clear-selection')
      }
    },

    refresh() {
      return this.loadData(this.lastSearchTerm);
    },

    clearOptions() {
      this.options = [];
      this.selectedItems = [];
    },

    search(term) {
      return this.loadData(term);
    }
  },
};
</script>

<style scoped>
.options-search-responsive {
  font-size: 14px;
}
.label-input-selected {
  min-height: 17.5px;
  display: block;
}

.p-float-label .p-inputwrapper-focus ~ label,
.p-float-label .p-inputwrapper-filled ~ label {
  top: -0.75rem !important;
  left: 0rem !important;
  font-size: 12px !important;
}
</style>

<!-- <AppAutocomplete
  label="Selecionar Funcionários"
  v-model="task.userIds"
  :fetchFunction="fetchUsers"
  :resolveItemsFunction="fetchUsersByIds"
  :multiple="true"
/>

<AppAutocomplete
  label="Selecionar Funcionário"
  v-model="task.userId"
  :fetchFunction="fetchUsers"
  :editModel="task.userObject"
/> -->